import { createSignal, onMount, For, Show, createMemo } from "solid-js";
import { api, type Organization, type User } from "../shared";

const PAGE_SIZE = 20;

export function UserPanel() {
  const [users, setUsers] = createSignal<User[]>([]);
  const [organizations, setOrganizations] = createSignal<Organization[]>([]);
  const [page, setPage] = createSignal(1);
  const [error, setError] = createSignal("");
  const [success, setSuccess] = createSignal("");
  const [filterRole, setFilterRole] = createSignal("");
  const [filterStatus, setFilterStatus] = createSignal("");
  const [filterOrganizationId, setFilterOrganizationId] = createSignal("");

  const [showAdd, setShowAdd] = createSignal(false);
  const [editUser, setEditUser] = createSignal<User | null>(null);
  const [resetPwUser, setResetPwUser] = createSignal<User | null>(null);
  const [loginLinkUser, setLoginLinkUser] = createSignal<User | null>(null);
  const [loginLinkData, setLoginLinkData] = createSignal<{ link: string; expiresAt: string } | null>(null);
  const [loginLinkExpiry, setLoginLinkExpiry] = createSignal(300);
  const [verifyLinkUser, setVerifyLinkUser] = createSignal<User | null>(null);
  const [verifyLinkData, setVerifyLinkData] = createSignal<{ link: string; expiresAt: string } | null>(null);

  const totalPages = createMemo(() => Math.max(1, Math.ceil((users() || []).length / PAGE_SIZE)));
  const pageUsers = createMemo(() => {
    const start = (page() - 1) * PAGE_SIZE;
    return (users() || []).slice(start, start + PAGE_SIZE);
  });

  async function load() {
    try {
      const filters: { role?: string; status?: string; organizationId?: string } = {};
      const r = filterRole();
      const s = filterStatus();
      const orgId = filterOrganizationId();
      if (r) filters.role = r;
      if (s) filters.status = s;
      if (orgId) filters.organizationId = orgId;
      setUsers(await api.users.list(filters));
      setPage(1);
      setError("");
    } catch (e: any) {
      setError(e.message);
    }
  }

  onMount(async () => {
    try {
      const [userRows, orgRows] = await Promise.all([
        api.users.list(),
        api.organizations.list(),
      ]);
      setUsers(userRows);
      setOrganizations(orgRows);
      setPage(1);
      setError("");
    } catch (e: any) {
      setError(e.message);
    }
  });

  function notify(msg: string) {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3000);
  }

  return (
    <div class="space-y-6">
      <Show when={error()}>
        <div class="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">{error()}</div>
      </Show>
      <Show when={success()}>
        <div class="bg-emerald-900/30 border border-emerald-700 text-emerald-300 px-4 py-3 rounded-lg text-sm">{success()}</div>
      </Show>

      <div class="flex items-center justify-between flex-wrap gap-3">
        <h2 class="text-lg font-semibold">Users ({users().length})</h2>
        <div class="flex items-center gap-3">
          <select
            value={filterRole()}
            onChange={(e) => { setFilterRole(e.currentTarget.value); load(); }}
            class="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"
          >
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="searcher">Searcher</option>
          </select>
          <select
            value={filterStatus()}
            onChange={(e) => { setFilterStatus(e.currentTarget.value); load(); }}
            class="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"
          >
            <option value="">All status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>
          <select
            value={filterOrganizationId()}
            onChange={(e) => { setFilterOrganizationId(e.currentTarget.value); load(); }}
            class="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"
          >
            <option value="">All organizations</option>
            <For each={organizations()}>
              {(org) => <option value={org.id}>{org.name}</option>}
            </For>
          </select>
          <select
            value={loginLinkExpiry()}
            onChange={(e) => setLoginLinkExpiry(Number(e.currentTarget.value))}
            class="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"
            title="Login link expiry"
          >
            <option value={300}>Link: 5 min</option>
            <option value={900}>Link: 15 min</option>
            <option value={3600}>Link: 1 hour</option>
            <option value={14400}>Link: 4 hours</option>
            <option value={86400}>Link: 24 hours</option>
            <option value={172800}>Link: 2 days</option>
            <option value={259200}>Link: 3 days</option>
            <option value={604800}>Link: 7 days</option>
          </select>
          <button
            onClick={() => setShowAdd(true)}
            class="px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-500 transition-colors cursor-pointer"
          >
            + Add Admin
          </button>
        </div>
      </div>

      <div class="border border-gray-800 rounded-lg overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-900 text-gray-400">
            <tr>
              <th class="text-left px-4 py-3 font-medium">Email</th>
              <th class="text-left px-4 py-3 font-medium">Display Name</th>
              <th class="text-left px-4 py-3 font-medium">Profile</th>
              <th class="text-left px-4 py-3 font-medium">Verification</th>
              <th class="text-left px-4 py-3 font-medium">Role</th>
              <th class="text-left px-4 py-3 font-medium">Status</th>
              <th class="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-800">
            <For each={pageUsers()}>
              {(u) => (
                <tr class="hover:bg-gray-900/50 transition-colors">
                  <td class="px-4 py-3 text-gray-200">{u.email}</td>
                  <td class="px-4 py-3 text-gray-300">{u.displayName}</td>
                  <td class="px-4 py-3 text-gray-400">{u.profileLocked ? "Locked" : "Editable"}</td>
                  <td class="px-4 py-3 text-xs">
                    {u.emailVerified ? (
                      <span class="rounded border border-emerald-700 bg-emerald-900/40 px-2 py-0.5 text-emerald-300">Verified</span>
                    ) : (
                      <span class="rounded border border-amber-700 bg-amber-900/40 px-2 py-0.5 text-amber-300">Unverified</span>
                    )}
                  </td>
                  <td class="px-4 py-3">
                    <RoleBadge role={u.role} />
                  </td>
                  <td class="px-4 py-3">
                    <StatusBadge status={u.status} />
                  </td>
                  <td class="px-4 py-3 text-right space-x-1">
                    <button title="Edit this user" onClick={() => setEditUser(u)} class="text-xs px-2 py-1 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors cursor-pointer">Edit</button>
                    <button title="Reset this user's password" onClick={() => setResetPwUser(u)} class="text-xs px-2 py-1 bg-gray-800 text-amber-400 rounded hover:bg-gray-700 transition-colors cursor-pointer">Reset PW</button>
                    <button onClick={async () => {
                      try {
                        const result = await api.users.loginLink(u.id, undefined, loginLinkExpiry());
                        setLoginLinkData({ link: result.link, expiresAt: result.expiresAt });
                        setLoginLinkUser(u);
                      } catch (e: any) { setError(e.message); }
                    }} title="Generate a one-time login link" class="text-xs px-2 py-1 bg-gray-800 text-cyan-400 rounded hover:bg-gray-700 transition-colors cursor-pointer">Link</button>
                    <Show when={!u.emailVerified}>
                      <button onClick={async () => {
                        try {
                          const result = await api.users.emailVerificationLink(u.id);
                          setVerifyLinkData({ link: result.link, expiresAt: result.expiresAt });
                          setVerifyLinkUser(u);
                        } catch (e: any) { setError(e.message); }
                      }} title="Generate an email verification link" class="text-xs px-2 py-1 bg-gray-800 text-emerald-400 rounded hover:bg-gray-700 transition-colors cursor-pointer">Verify</button>
                    </Show>
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>

      <Show when={totalPages() > 1}>
        <div class="flex items-center justify-center gap-2">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page() === 1} class="px-3 py-1 text-sm bg-gray-800 rounded disabled:opacity-30 cursor-pointer">Prev</button>
          <span class="text-sm text-gray-400">Page {page()} of {totalPages()}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages(), p + 1))} disabled={page() >= totalPages()} class="px-3 py-1 text-sm bg-gray-800 rounded disabled:opacity-30 cursor-pointer">Next</button>
        </div>
      </Show>

      <Show when={showAdd()}><AddAdminModal onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); notify("Admin created"); }} /></Show>
      <Show when={editUser()}><EditUserModal user={editUser()!} onClose={() => setEditUser(null)} onDone={() => { setEditUser(null); load(); notify("User updated"); }} /></Show>
      <Show when={resetPwUser()}><ResetPasswordModal user={resetPwUser()!} onClose={() => { setResetPwUser(null); setLoginLinkData(null); }} onDone={() => { setResetPwUser(null); notify("Password reset"); }} /></Show>
      <Show when={loginLinkUser() && loginLinkData()}>
        <LoginLinkModal user={loginLinkUser()!} link={loginLinkData()!.link} expiresAt={loginLinkData()!.expiresAt} onClose={() => { setLoginLinkUser(null); setLoginLinkData(null); }} />
      </Show>
      <Show when={verifyLinkUser() && verifyLinkData()}>
        <VerificationLinkModal user={verifyLinkUser()!} link={verifyLinkData()!.link} expiresAt={verifyLinkData()!.expiresAt} onClose={() => { setVerifyLinkUser(null); setVerifyLinkData(null); }} />
      </Show>
    </div>
  );
}

function RoleBadge(props: { role: string }) {
  const colors: Record<string, string> = {
    admin: "bg-purple-900/40 text-purple-300 border-purple-700",
    manager: "bg-blue-900/40 text-blue-300 border-blue-700",
    searcher: "bg-gray-800 text-gray-400 border-gray-700",
  };
  return <span class={`px-2 py-0.5 text-xs font-medium rounded border ${colors[props.role] ?? colors.searcher}`}>{props.role}</span>;
}

function StatusBadge(props: { status: string }) {
  const colors: Record<string, string> = {
    active: "bg-emerald-900/40 text-emerald-300 border-emerald-700",
    pending: "bg-amber-900/40 text-amber-300 border-amber-700",
    suspended: "bg-red-900/40 text-red-300 border-red-700",
  };
  return <span class={`px-2 py-0.5 text-xs font-medium rounded border ${colors[props.status] ?? colors.pending}`}>{props.status}</span>;
}

function AddAdminModal(props: { onClose: () => void; onDone: () => void }) {
  const [email, setEmail] = createSignal("");
  const [displayName, setDisplayName] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [orgName, setOrgName] = createSignal("");
  const [createOrg, setCreateOrg] = createSignal(false);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal("");
  async function submit(e: Event) {
    e.preventDefault(); setLoading(true); setError("");
    try {
      await api.users.create({
        email: email(),
        displayName: displayName() || "Admin",
        password: password(),
        organizationName: createOrg() ? orgName() || undefined : undefined,
      });
      props.onDone();
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }
  return (
    <Modal onClose={props.onClose}>
      <h3 class="text-lg font-semibold mb-4">Add Admin User</h3>
      <Show when={error()}><div class="bg-red-900/30 border border-red-700 text-red-300 px-3 py-2 rounded text-sm mb-3">{error()}</div></Show>
      <form onSubmit={submit} class="space-y-4">
        <Field label="Email" value={email()} onChange={setEmail} type="email" required />
        <Field label="Display Name" value={displayName()} onChange={setDisplayName} required />
        <Field label="Password" value={password()} onChange={setPassword} type="password" required />
        <label class="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={createOrg()} onChange={(e) => setCreateOrg(e.currentTarget.checked)} class="rounded bg-gray-800 border-gray-700 text-cyan-600" />
          <span class="text-sm text-gray-400">Create Organization</span>
        </label>
        <Show when={createOrg()}>
          <Field label="Organization Name" value={orgName()} onChange={setOrgName} required />
        </Show>
        <div class="flex justify-end gap-3 pt-2">
          <button type="button" onClick={props.onClose} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button>
          <button type="submit" disabled={loading()} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">{loading() ? "Creating..." : "Create Admin"}</button>
        </div>
      </form>
    </Modal>
  );
}

function EditUserModal(props: { user: User; onClose: () => void; onDone: () => void }) {
  const [role, setRole] = createSignal(props.user.role);
  const [status, setStatus] = createSignal(props.user.status);
  const [displayName, setDisplayName] = createSignal(props.user.displayName ?? "");
  const [profileLocked, setProfileLocked] = createSignal(!!props.user.profileLocked);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal("");
  async function submit(e: Event) {
    e.preventDefault(); setLoading(true); setError("");
    try { await api.users.update(props.user.id, { role: role() !== props.user.role ? role() : undefined, status: status() !== props.user.status ? status() : undefined, displayName: displayName() !== props.user.displayName ? displayName() : undefined, profileLocked: profileLocked() !== !!props.user.profileLocked ? profileLocked() : undefined }); props.onDone(); }
    catch (e: any) { setError(e.message); }
    setLoading(false);
  }
  return (
    <Modal onClose={props.onClose}>
      <h3 class="text-lg font-semibold mb-1">Edit User</h3>
      <p class="text-sm text-gray-400 mb-4">{props.user.email}</p>
      <Show when={error()}><div class="bg-red-900/30 border border-red-700 text-red-300 px-3 py-2 rounded text-sm mb-3">{error()}</div></Show>
      <form onSubmit={submit} class="space-y-4">
        <Field label="Display Name" value={displayName()} onChange={setDisplayName} required />
        <label class="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={profileLocked()} onChange={(e) => setProfileLocked(e.currentTarget.checked)} class="rounded bg-gray-800 border-gray-700 text-cyan-600" />
          <span class="text-sm text-gray-400">Lock profile changes for this user</span>
        </label>
        <div><label class="block text-sm text-gray-400 mb-1">Role</label><select value={role()} onChange={(e) => setRole(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="admin">Admin</option><option value="manager">Manager</option><option value="searcher">Searcher</option></select></div>
        <div><label class="block text-sm text-gray-400 mb-1">Status</label><select value={status()} onChange={(e) => setStatus(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="active">Active</option><option value="pending">Pending</option><option value="suspended">Suspended</option></select></div>
        <div class="flex justify-end gap-3 pt-2">
          <button type="button" onClick={props.onClose} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button>
          <button type="submit" disabled={loading()} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">{loading() ? "Saving..." : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}

function ResetPasswordModal(props: { user: User; onClose: () => void; onDone: () => void }) {
  const [password, setPassword] = createSignal("");
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal("");
  async function submit(e: Event) {
    e.preventDefault(); setLoading(true); setError("");
    try { await api.users.resetPassword(props.user.id, password()); props.onDone(); }
    catch (e: any) { setError(e.message); }
    setLoading(false);
  }
  return (
    <Modal onClose={props.onClose}>
      <h3 class="text-lg font-semibold mb-1">Reset Password</h3>
      <p class="text-sm text-gray-400 mb-4">{props.user.email}</p>
      <Show when={error()}><div class="bg-red-900/30 border border-red-700 text-red-300 px-3 py-2 rounded text-sm mb-3">{error()}</div></Show>
      <form onSubmit={submit} class="space-y-4">
        <Field label="New Password" value={password()} onChange={setPassword} type="password" required minlength={4} />
        <div class="flex justify-end gap-3 pt-2">
          <button type="button" onClick={props.onClose} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button>
          <button type="submit" disabled={loading()} class="px-4 py-2 bg-amber-600 text-white rounded text-sm font-medium hover:bg-amber-500 disabled:opacity-50 cursor-pointer">{loading() ? "Resetting..." : "Reset Password"}</button>
        </div>
      </form>
    </Modal>
  );
}

function LoginLinkModal(props: { user: User; link: string; expiresAt: string; onClose: () => void }) {
  const [copied, setCopied] = createSignal(false);

  function copy() {
    navigator.clipboard.writeText(props.link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  }

  return (
    <Modal onClose={props.onClose}>
      <h3 class="text-lg font-semibold mb-1">One-Time Login Link</h3>
      <p class="text-sm text-gray-400 mb-4">{props.user.email} — expires {new Date(props.expiresAt).toLocaleTimeString()}</p>
      <div class="bg-gray-800 rounded px-3 py-2 text-xs text-gray-300 break-all mb-3 font-mono">{props.link}</div>
      <div class="flex justify-end gap-3">
        <button onClick={props.onClose} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Close</button>
        <button onClick={copy} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 cursor-pointer">{copied() ? "Copied!" : "Copy Link"}</button>
      </div>
    </Modal>
  );
}

function VerificationLinkModal(props: { user: User; link: string; expiresAt: string; onClose: () => void }) {
  const [copied, setCopied] = createSignal(false);
  function copy() {
    navigator.clipboard.writeText(props.link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  }
  return (
    <Modal onClose={props.onClose}>
      <h3 class="text-lg font-semibold mb-1">Email Verification Link</h3>
      <p class="text-sm text-gray-400 mb-4">{props.user.email} - expires {new Date(props.expiresAt).toLocaleString()}</p>
      <div class="bg-gray-800 rounded px-3 py-2 text-xs text-gray-300 break-all mb-3 font-mono">{props.link}</div>
      <div class="flex justify-end gap-3">
        <button onClick={props.onClose} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Close</button>
        <button onClick={copy} class="px-4 py-2 bg-emerald-600 text-white rounded text-sm font-medium hover:bg-emerald-500 cursor-pointer">{copied() ? "Copied!" : "Copy Link"}</button>
      </div>
    </Modal>
  );
}

function Field(props: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean; minlength?: number }) {
  return (
    <div>
      <label class="block text-sm text-gray-400 mb-1">{props.label}</label>
      <input type={props.type ?? "text"} value={props.value} onInput={(e) => props.onChange(e.currentTarget.value)} required={props.required} minlength={props.minlength} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" />
    </div>
  );
}

function Modal(props: { onClose: () => void; children: any }) {
  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black/60" onClick={props.onClose} />
      <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-md shadow-xl z-10">{props.children}</div>
    </div>
  );
}
